/**
 * @fileoverview Popover control for updating VAT metadata on a payment row.
 * @module features/payments/components/PaymentVatPopover
 */

import { useState, type ReactElement } from 'react'
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/shared/lib/utils'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { formatVatPercent } from '@/shared/utils/vat'
import type { PaymentsPageResponse, PaymentDTO } from '../schemas/paymentsSchema'
import { overrideUniversalTransaction } from '@/features/universal-transactions/api/universalTransactions'
import {
  buildVatOverridePayload,
  type VatMutationPayload,
  type VatSelectionState,
} from '../utils/vatOverride'

interface VatPreset {
  /** Unique key used for i18n lookup */
  key: 'standard' | 'reverseCharge' | 'noVat'
  /** Desired VAT state after applying the preset */
  next: VatSelectionState
}

const VAT_PRESETS: VatPreset[] = [
  {
    key: 'standard',
    next: { vatApplicable: true, reverseCharge: false, vatRate: 0.23 },
  },
  {
    key: 'reverseCharge',
    next: { vatApplicable: true, reverseCharge: true, vatRate: 0 },
  },
  {
    key: 'noVat',
    next: { vatApplicable: false, reverseCharge: false, vatRate: null },
  },
]

export interface PaymentVatPopoverProps {
  /** Payment row being edited */
  payment: PaymentDTO
  /** Optional class to control trigger sizing/alignment */
  className?: string
  /** Render trigger and label in a low-emphasis style */
  quiet?: boolean
}

/**
 * PaymentVatPopover – wraps VAT cell with a dropdown allowing the user to pick presets or enter a custom rate.
 *
 * @component
 * @example
 * ```tsx
 * <PaymentVatPopover payment={payment} />
 * ```
 */
export default function PaymentVatPopover({
  payment,
  className,
  quiet = false,
}: PaymentVatPopoverProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const queryClient = useQueryClient()
  const [customOpen, setCustomOpen] = useState(false)
  const [customRate, setCustomRate] = useState(
    typeof payment.vatRate === 'number' ? (payment.vatRate * 100).toFixed(2) : ''
  )
  const [customReverseCharge, setCustomReverseCharge] = useState(payment.reverseCharge)
  const [customError, setCustomError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async ({ apiPayload, nextState }: VatMutationPayload) => {
      await overrideUniversalTransaction(payment.id, apiPayload)
      return nextState
    },
    onMutate: async ({ nextState }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['payments'] })

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueriesData<InfiniteData<PaymentsPageResponse>>({
        queryKey: ['payments'],
      })

      // Optimistically update the cache immediately
      queryClient.setQueriesData<InfiniteData<PaymentsPageResponse>>(
        { queryKey: ['payments'] },
        data => {
          if (!data || !data.pages) return data

          return {
            ...data,
            pages: data.pages.map(page => {
              if (!page || !page.content) return page

              return {
                ...page,
                content: page.content.map(item =>
                  item.id === payment.id
                    ? {
                        ...item,
                        vatApplicable: nextState.vatApplicable,
                        reverseCharge: nextState.reverseCharge,
                        vatRate: nextState.vatRate,
                      }
                    : item
                ),
              }
            }),
          }
        }
      )

      // Return context with snapshot for rollback
      return { previousData }
    },
    onSuccess: () => {
      // VAT/classification changes can alter indirect costs amounts on the server.
      // Ensure IndirectCostsPage reflects new amounts without a hard reload.
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      setCustomOpen(false)
    },
    onError: (error: unknown, _variables, context) => {
      // Rollback to snapshot on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      const message = error instanceof Error ? error.message : t('vatPopover.toasts.error')
      toast.error(message)
    },
    onSettled: () => {
      // Mark stale but don't refetch active queries — the optimistic update
      // already has the correct data and a refetch would re-sort items causing jumps.
      void queryClient.invalidateQueries({ queryKey: ['payments'], refetchType: 'none' })
    },
  })

  const handlePreset = (preset: VatPreset): void => {
    setCustomError(null)
    const payload = buildVatOverridePayload(preset.next)
    mutation.mutate(payload)
  }

  const handleCustomSubmit = (): void => {
    const numeric = Number.parseFloat(customRate.replace(',', '.'))
    if (!Number.isFinite(numeric) || numeric < 0) {
      setCustomError(t('vatPopover.custom.error'))
      return
    }

    if (numeric > 100) {
      setCustomError(t('vatPopover.custom.error'))
      return
    }

    setCustomError(null)
    const fraction = numeric / 100
    const selection: VatSelectionState = {
      vatApplicable: true,
      reverseCharge: customReverseCharge,
      vatRate: fraction,
    }

    const payload = buildVatOverridePayload(selection)
    mutation.mutate(payload)
  }

  let labelText = ''
  if (payment.vatApplicable === null) {
    // Not set – keep the orange hint from previous UX
    labelText = 'VAT rate?'
  } else if (payment.vatApplicable === false) {
    labelText = t('vatPopover.labels.noVat', { defaultValue: 'No VAT' })
  } else if (payment.reverseCharge) {
    labelText = '0% VAT'
  } else {
    const pct = typeof payment.vatRate === 'number' ? formatVatPercent(payment.vatRate) : '0%'
    labelText = `${pct} VAT`
  }

  const needsVatAttention = payment.vatApplicable === null

  const textClass = quiet
    ? 'text-muted-foreground/80 font-medium'
    : needsVatAttention
      ? 'text-yellow-500 font-medium'
      : payment.vatApplicable === false
        ? 'text-muted-foreground'
        : ''

  const triggerToneClass = quiet
    ? 'border-transparent bg-transparent hover:border-border/50 hover:bg-muted/15'
    : needsVatAttention
      ? 'border-yellow-500/55 bg-yellow-500/10 hover:bg-yellow-500/15'
      : payment.vatApplicable === false
        ? 'border-border/70 bg-muted/20 hover:bg-muted/35'
        : payment.reverseCharge
          ? 'border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/15'
          : 'border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15'

  const percentText =
    payment.vatApplicable === null
      ? null
      : payment.vatApplicable === false
        ? null
        : payment.reverseCharge
          ? '0%'
          : typeof payment.vatRate === 'number'
            ? formatVatPercent(payment.vatRate)
            : '0%'

  const vatLabel = (
    <span className={cn('inline-flex items-baseline gap-1', quiet ? 'text-xs' : 'text-sm')}>
      {percentText ? (
        <>
          <span className={cn(textClass, quiet ? 'text-sm font-semibold' : 'text-base font-semibold')}>
            {percentText}
          </span>
          <span className="text-[0.85em] uppercase tracking-wide text-muted-foreground leading-none">
            VAT
          </span>
        </>
      ) : (
        <span className={cn('inline-flex items-center gap-1.5', textClass)}>
          {quiet && needsVatAttention ? (
            <span className="size-1.5 rounded-full bg-yellow-400 ring-2 ring-yellow-500/20" aria-hidden />
          ) : null}
          <span>{labelText}</span>
        </span>
      )}
    </span>
  )

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              quiet ? 'min-w-0 px-1.5 justify-center gap-1.5 transition-colors' : 'min-w-[5.25rem] px-2 justify-center gap-2 transition-colors',
              triggerToneClass,
              className,
            )}
            aria-label={t('vatPopover.triggerAria')}
            type="button"
          >
            {vatLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-36 p-0">
          <DropdownMenuLabel className="rounded-t-md bg-muted/50 px-3 py-1.5 text-xs font-normal text-muted-foreground">
            {t('vatPopover.title')}
          </DropdownMenuLabel>
          <div className="divide-y divide-border/50 p-1">
            <DropdownMenuItem
              disabled={mutation.isPending}
              onSelect={() => handlePreset(VAT_PRESETS.find(p => p.key === 'standard')!)}
            >
              {`${formatVatPercent(0.23)} VAT`}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={mutation.isPending}
              onSelect={() => handlePreset(VAT_PRESETS.find(p => p.key === 'reverseCharge')!)}
              className="flex-col items-start gap-0.5"
            >
              <span>0% VAT</span>
              <span className="text-xs text-muted-foreground">
                ({t('vatPopover.labels.reverseCharge', { defaultValue: 'reverse charge' })})
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={mutation.isPending}
              onSelect={() => handlePreset(VAT_PRESETS.find(p => p.key === 'noVat')!)}
            >
              {t('vatPopover.labels.noVat', { defaultValue: 'No VAT' })}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={mutation.isPending}
              onSelect={() => setCustomOpen(true)}
            >
              {t('vatPopover.preset.custom')}
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('vatPopover.title')}</DialogTitle>
            <DialogDescription>
              {t('vatPopover.custom.label', { defaultValue: 'Enter a custom VAT rate.' })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                htmlFor={`payment-${payment.id}-vat-rate`}
              >
                {t('vatPopover.custom.label')}
              </label>
              <Input
                id={`payment-${payment.id}-vat-rate`}
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                max="100"
                value={customRate}
                onChange={event => setCustomRate(event.target.value)}
                disabled={mutation.isPending}
                autoFocus
              />
              {customError ? <p className="text-xs text-destructive">{customError}</p> : null}
            </div>

            <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
              <span className="text-sm text-muted-foreground">
                {t('vatPopover.custom.reverseChargeLabel')}
              </span>
              <Button
                type="button"
                size="sm"
                variant={customReverseCharge ? 'default' : 'outline'}
                onClick={() => setCustomReverseCharge(prev => !prev)}
                aria-pressed={customReverseCharge}
                disabled={mutation.isPending}
              >
                {customReverseCharge
                  ? t('vatPopover.custom.reverseChargeOn')
                  : t('vatPopover.custom.reverseChargeOff')}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCustomOpen(false)
                setCustomError(null)
              }}
              disabled={mutation.isPending}
            >
              {t('vatPopover.custom.cancel')}
            </Button>
            <Button type="button" onClick={handleCustomSubmit} disabled={mutation.isPending}>
              {mutation.isPending ? t('vatPopover.custom.saving') : t('vatPopover.custom.apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
